import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import type { BaseBody, DefinitionSummary } from "../generic/api";

export type ServiceModuleKind = "Compiled" | "Dynamic";
export type ServiceAudience = "External" | "Internal" | "Any";

export interface RequiredDocument { key: string; nameEn: string; nameAr?: string | null; required: boolean; accepts?: string | null }

/** Settings a Dynamic service keeps as JSON (the AI Workspace playbook reads the same object). */
export interface ServiceSettings {
  workspaceKey?: string | null;
  [key: string]: unknown;
}

export interface ServiceBody extends BaseBody {
  moduleKind: ServiceModuleKind;
  category: string;
  categoryAr?: string | null;
  audience: ServiceAudience;
  icon: string;
  summary?: string | null;
  summaryAr?: string | null;
  legalBasis?: string | null;
  legalBasisAr?: string | null;
  processingTime?: string | null;
  processingTimeAr?: string | null;
  legacyCaseType?: string | null;
  intakeFormKey?: string | null;
  workflowKey: string;
  feeKeys: string[];
  templateKeys: string[];
  agentKeys: string[];
  eligibilityRuleKeys: string[];
  automationKeys: string[];
  caseNumberPattern: string;
  code: string;
  portalVisible: boolean;
  sortOrder: number;
  accessRoleKeys: string[];
  startRoleKeys: string[];
  intakeFlagKey?: string | null;
  requiredDocuments: RequiredDocument[];
  settings: ServiceSettings;
}

export interface ServiceSummary extends DefinitionSummary {
  moduleKind: ServiceModuleKind;
  audience: ServiceAudience;
  category: string;
  categoryAr?: string | null;
  code: string;
  icon: string;
  workflowKey: string;
  intakeFormKey?: string | null;
  workspaceKey?: string | null;
  portalVisible: boolean;
  openMatters: number;
  totalMatters: number;
}

export interface OptionRef { key: string; name: string; nameAr?: string | null; published?: boolean; live?: boolean; departmentKey?: string | null }
export interface ServiceOptions {
  workflows: OptionRef[];
  forms: OptionRef[];
  rules: OptionRef[];
  agents: OptionRef[];
  automations: OptionRef[];
  recordTypes: OptionRef[];
  roles: OptionRef[];
  workspaces: OptionRef[];
  fees: OptionRef[];
  templates: OptionRef[];
}

export const SERVICE_ICONS = ["FileText", "ClipboardList", "Scale", "Landmark", "LineChart", "Building2", "BadgeCheck", "FileWarning", "Gavel", "Handshake", "Briefcase", "Globe2"] as const;

/** A 2–6 character code from a name: the initials of up to four words, else the first letters. */
export function codeFromName(name: string): string {
  const words = name.normalize("NFKD").replace(/[^A-Za-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  const initials = words.slice(0, 4).map((w) => w[0]!.toUpperCase()).join("");
  const code = initials.length >= 2 ? initials : (words[0] ?? "SV").slice(0, 3).toUpperCase();
  return code.padEnd(2, "X").slice(0, 6);
}

export function newServiceBody(name: string, nameAr: string | null, extra: Record<string, string>): ServiceBody {
  return {
    name, nameAr, description: null, descriptionAr: null, tags: [],
    moduleKind: "Dynamic", category: extra.category || "General", categoryAr: null, audience: (extra.audience as ServiceAudience) || "External", icon: "FileText",
    summary: null, summaryAr: null, legalBasis: null, legalBasisAr: null, processingTime: null, processingTimeAr: null, legacyCaseType: null,
    intakeFormKey: null, workflowKey: "", feeKeys: [], templateKeys: [], agentKeys: [], eligibilityRuleKeys: [], automationKeys: [],
    caseNumberPattern: "{code}-{yyyy}-{seq:0000}", code: (extra.code || codeFromName(name)).toUpperCase(), portalVisible: true, sortOrder: 100,
    accessRoleKeys: [], startRoleKeys: [], intakeFlagKey: null, requiredDocuments: [], settings: {},
  };
}

export function useServiceOptions() {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "services", "options"], queryFn: () => api.get<ServiceOptions>("/api/platform/services/options"), staleTime: 30_000 });
}

export function useNumberPreview(pattern: string, code: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["platform", "services", "number-preview", pattern, code],
    queryFn: () => api.get<{ issues: string[]; examples: string[] }>("/api/platform/services/number-preview", { pattern, code }),
    staleTime: 60_000,
  });
}
